// ── Assignee picker dropdown ──

import { escapeHtml, renderAvatarHtml } from '../utils.js';

let currentDropdown = null;
let outsideHandler = null;
// Token of the showAssigneePicker() call that owns the open dropdown (TPT647). Each show returns
// a fresh one; removeAssigneePicker(token) acts only while that token still owns, so a page
// teardown that runs late cannot close the picker of the page now on screen.
let pickerOwner = null;

// With a token it acts only while that token owns the picker (null never does); no argument
// removes whatever picker is open.
export function removeAssigneePicker(token) {
  if (token !== undefined && token !== pickerOwner) return;
  pickerOwner = null;
  if (currentDropdown) {
    currentDropdown.remove();
    currentDropdown = null;
  }
  if (outsideHandler) {
    document.removeEventListener('mousedown', outsideHandler);
    outsideHandler = null;
  }
}

function renderOptions(members, currentAssignee, filter) {
  const q = (filter || '').toLowerCase();
  // Pending invites (user_id/name null) never resolve to a real assignee — dropping
  // them here avoids the old `m.user_id || m.id` fallback below landing on the
  // invite row's own id (a bogus assignee, C1252 fix).
  const accepted = members.filter(m => m.user_id != null && m.name != null);
  const filtered = q
    ? accepted.filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    : accepted;

  let html = '';

  // Unassigned option — always offered, never filtered out. Previously gated on
  // `'unassigned'.includes(q)`, which hid it as soon as a member name was typed,
  // leaving no way to clear the assignee via search (C1252 fix).
  {
    const active = currentAssignee == null ? ' assignee-option-active' : '';
    html += `<div class="assignee-option${active}" data-user-id="">`;
    html += renderAvatarHtml(null, 32);
    html += `<span class="assignee-option-name">Unassigned</span>`;
    html += `</div>`;
  }

  for (const m of filtered) {
    const uid = m.user_id;
    const active = uid === currentAssignee ? ' assignee-option-active' : '';
    html += `<div class="assignee-option${active}" data-user-id="${uid}">`;
    html += renderAvatarHtml(m, 32);
    html += `<span class="assignee-option-name">${escapeHtml(m.name || m.email || 'Unknown')}</span>`;
    if (m.role) html += `<span class="assignee-option-role">${escapeHtml(m.role)}</span>`;
    html += `</div>`;
  }

  if (!html) {
    html = `<div class="assignee-option" style="color:var(--c-text-faint);cursor:default;">No matches</div>`;
  }
  return html;
}

// Returns this picker's owner token (TPT647) — the caller keeps it for its teardown.
export function showAssigneePicker(anchorEl, { members, currentAssignee, onSelect }) {
  removeAssigneePicker();
  const token = {};
  pickerOwner = token;

  const rect = anchorEl.getBoundingClientRect();
  const dropdown = document.createElement('div');
  dropdown.className = 'assignee-dropdown';

  // Position: below anchor, flip above if near bottom
  const spaceBelow = window.innerHeight - rect.bottom;
  if (spaceBelow < 320) {
    dropdown.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
    dropdown.style.left = rect.left + 'px';
  } else {
    dropdown.style.top = (rect.bottom + 4) + 'px';
    dropdown.style.left = rect.left + 'px';
  }

  dropdown.innerHTML = `
    <div class="assignee-dropdown-search">
      <input type="text" placeholder="Search members..." autocomplete="off">
    </div>
    <div class="assignee-options">${renderOptions(members, currentAssignee, '')}</div>`;

  document.body.appendChild(dropdown);
  currentDropdown = dropdown;

  const searchInput = dropdown.querySelector('input');
  const optionsContainer = dropdown.querySelector('.assignee-options');

  searchInput.focus();

  searchInput.addEventListener('input', () => {
    optionsContainer.innerHTML = renderOptions(members, currentAssignee, searchInput.value);
  });

  dropdown.addEventListener('click', (e) => {
    const option = e.target.closest('.assignee-option');
    if (!option) return;
    const raw = option.dataset.userId;
    const userId = raw === '' ? null : parseInt(raw, 10);
    removeAssigneePicker(token);
    onSelect(userId);
  });

  // Dismiss on outside click. Deferred so the opening mousedown doesn't close it at once; skipped
  // when this picker was removed or replaced before the timeout fired.
  setTimeout(() => {
    if (pickerOwner !== token) return;
    outsideHandler = (e) => {
      if (!dropdown.contains(e.target)) {
        removeAssigneePicker(token);
      }
    };
    document.addEventListener('mousedown', outsideHandler);
  }, 0);

  return token;
}
